import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CategoryTabs from '@/components/clients/category-tabs';
import ClientsFiltersSheet from '@/components/clients/clients-filters-sheet';
import OnlineFilterGroup from '@/components/navigation/online-filter-group';
import { getCategoryConfig } from '@/entities/clients-categories';
import {
	$balancesSearchField,
	$balancesSortBy,
	$cardsSearchField,
	$cardsSortBy,
	$clientsFilter,
	$clientsListType,
	$clientsSearchField,
	$clientsSearchText,
	$clientsSortBy,
	$clientsSortDir,
	$messagesSearchField,
	$messagesSortBy,
	$pushesSearchField,
	$pushesSortBy,
	$deviceLogsSearchField,
	$deviceLogsSortBy,
	$ussdSearchField,
	$ussdSortBy,
	$phonesSearchField,
	$phonesSortBy,
	$setBalancesSearchField,
	$setBalancesSortBy,
	$setCardsSearchField,
	$setCardsSortBy,
	$setClientsListType,
	$setClientsSearchField,
	$setClientsSearchText,
	$setClientsSortBy,
	$setClientsSortDir,
	$setMessagesSearchField,
	$setMessagesSortBy,
	$setPushesSearchField,
	$setPushesSortBy,
	$setDeviceLogsSearchField,
	$setDeviceLogsSortBy,
	$setUssdSearchField,
	$setUssdSortBy,
	$setPhonesSearchField,
	$setPhonesSortBy,
	$updateClientsFilter,
	clearFilterKey,
	countActiveClientsFilters,
	defaultClientsFilter,
	getActiveFilterChips,
	type BalancesSearchField,
	type BalancesSortBy,
	type CardsSearchField,
	type CardsSortBy,
	type ClientsListType,
	type ClientsSearchField,
	type ClientsSortBy,
	type MessagesSearchField,
	type MessagesSortBy,
	type PushesSearchField,
	type PushesSortBy,
	type DeviceLogsSearchField,
	type DeviceLogsSortBy,
	type UssdSearchField,
	type UssdSortBy,
	type PhonesSearchField,
	type PhonesSortBy,
	type SortDir,
} from '@/entities/clients-filter.entity';
import { $onlineClientsCount } from '@/entities/online-count.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { useUnit } from 'effector-react';
import { ArrowDownWideNarrowIcon, ArrowUpWideNarrowIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export default function ClientsToolbar() {
	const [
		filter,
		updateFilter,
		onlineCount,
		listType,
		setListType,
		searchText,
		setSearchText,
		clientsSearchField,
		setClientsSearchField,
		balancesSearchField,
		setBalancesSearchField,
		cardsSearchField,
		setCardsSearchField,
		phonesSearchField,
		setPhonesSearchField,
		messagesSearchField,
		setMessagesSearchField,
		pushesSearchField,
		setPushesSearchField,
		ussdSearchField,
		setUssdSearchField,
		clientsSortBy,
		setClientsSortBy,
		balancesSortBy,
		setBalancesSortBy,
		cardsSortBy,
		setCardsSortBy,
		phonesSortBy,
		setPhonesSortBy,
		messagesSortBy,
		setMessagesSortBy,
		pushesSortBy,
		setPushesSortBy,
		deviceLogsSearchField,
		setDeviceLogsSearchField,
		deviceLogsSortBy,
		setDeviceLogsSortBy,
		ussdSortBy,
		setUssdSortBy,
		sortDir,
		setSortDir,
	] = useUnit([
		$clientsFilter,
		$updateClientsFilter,
		$onlineClientsCount,
		$clientsListType,
		$setClientsListType,
		$clientsSearchText,
		$setClientsSearchText,
		$clientsSearchField,
		$setClientsSearchField,
		$balancesSearchField,
		$setBalancesSearchField,
		$cardsSearchField,
		$setCardsSearchField,
		$phonesSearchField,
		$setPhonesSearchField,
		$messagesSearchField,
		$setMessagesSearchField,
		$pushesSearchField,
		$setPushesSearchField,
		$ussdSearchField,
		$setUssdSearchField,
		$clientsSortBy,
		$setClientsSortBy,
		$balancesSortBy,
		$setBalancesSortBy,
		$cardsSortBy,
		$setCardsSortBy,
		$phonesSortBy,
		$setPhonesSortBy,
		$messagesSortBy,
		$setMessagesSortBy,
		$pushesSortBy,
		$setPushesSortBy,
		$deviceLogsSearchField,
		$setDeviceLogsSearchField,
		$deviceLogsSortBy,
		$setDeviceLogsSortBy,
		$ussdSortBy,
		$setUssdSortBy,
		$clientsSortDir,
		$setClientsSortDir,
	]);

	const [filtersOpen, setFiltersOpen] = useState(false);

	const config = useMemo(() => getCategoryConfig(listType), [listType]);
	const activeFilters = countActiveClientsFilters(filter, config.filterSchema);
	const chips = useMemo(() => getActiveFilterChips(filter, config.filterSchema), [filter, config.filterSchema]);

	const searchField =
		listType === 'messages'
			? messagesSearchField
			: listType === 'pushes'
				? pushesSearchField
				: listType === 'deviceLogs'
					? deviceLogsSearchField
					: listType === 'ussd'
						? ussdSearchField
						: listType === 'balances'
							? balancesSearchField
							: listType === 'clients'
								? clientsSearchField
								: listType === 'cards'
									? cardsSearchField
									: phonesSearchField;
	const sortBy =
		listType === 'messages'
			? messagesSortBy
			: listType === 'pushes'
				? pushesSortBy
				: listType === 'deviceLogs'
					? deviceLogsSortBy
					: listType === 'ussd'
						? ussdSortBy
						: listType === 'balances'
							? balancesSortBy
							: listType === 'clients'
								? clientsSortBy
								: listType === 'cards'
									? cardsSortBy
									: phonesSortBy;
	const sortLabel = config.sortFields.find(f => f.value === sortBy)?.label ?? sortBy;
	const searchPlaceholder = config.searchFields.find(f => f.value === searchField)?.label ?? 'Значение';

	useEffect(() => {
		if (isDevMode) return;
		socket.emit(SOCKET_EVENTS.getOnlineCount);
	}, []);

	function setSearchField(v: string) {
		if (listType === 'messages') setMessagesSearchField(v as MessagesSearchField);
		else if (listType === 'pushes') setPushesSearchField(v as PushesSearchField);
		else if (listType === 'deviceLogs') setDeviceLogsSearchField(v as DeviceLogsSearchField);
		else if (listType === 'ussd') setUssdSearchField(v as UssdSearchField);
		else if (listType === 'balances') setBalancesSearchField(v as BalancesSearchField);
		else if (listType === 'clients') setClientsSearchField(v as ClientsSearchField);
		else if (listType === 'cards') setCardsSearchField(v as CardsSearchField);
		else setPhonesSearchField(v as PhonesSearchField);
	}

	function setSortBy(v: string) {
		if (listType === 'messages') setMessagesSortBy(v as MessagesSortBy);
		else if (listType === 'pushes') setPushesSortBy(v as PushesSortBy);
		else if (listType === 'deviceLogs') setDeviceLogsSortBy(v as DeviceLogsSortBy);
		else if (listType === 'ussd') setUssdSortBy(v as UssdSortBy);
		else if (listType === 'balances') setBalancesSortBy(v as BalancesSortBy);
		else if (listType === 'clients') setClientsSortBy(v as ClientsSortBy);
		else if (listType === 'cards') setCardsSortBy(v as CardsSortBy);
		else setPhonesSortBy(v as PhonesSortBy);
	}

	function handleCategory(next: ClientsListType) {
		setListType(next);
	}

	return (
		<div className='flex flex-col gap-2 border-b border-border pb-2 bg-background/95 backdrop-blur-sm'>
			<CategoryTabs value={listType} onChange={handleCategory} />

			<div className='flex gap-2'>
				<Select value={searchField} onValueChange={setSearchField}>
					<SelectTrigger className='w-[36%] max-w-[140px] shrink-0 h-9'>
						<SelectValue placeholder='Поле' />
					</SelectTrigger>
					<SelectContent>
						{config.searchFields.map(opt => (
							<SelectItem key={opt.value} value={opt.value}>
								{opt.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<InputGroup className='flex-1'>
					<InputGroupInput
						placeholder={searchPlaceholder}
						value={searchText}
						onChange={e => setSearchText(e.target.value)}
					/>
					{searchText ? (
						<InputGroupAddon align='inline-end'>
							<InputGroupButton size='icon-xs' aria-label='Очистить' onClick={() => setSearchText('')}>
								<XIcon />
							</InputGroupButton>
						</InputGroupAddon>
					) : null}
				</InputGroup>
			</div>

			<div className='flex items-center gap-1.5'>
				<OnlineFilterGroup
					compact
					className='flex-1 min-w-0'
					value={filter.onlineStatus}
					onChange={v => updateFilter({ onlineStatus: v })}
					onlineCount={onlineCount}
				/>

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='outline' size='sm' className='shrink-0 gap-1 max-w-[120px]'>
							{sortDir === 'asc' ? <ArrowUpWideNarrowIcon size={14} /> : <ArrowDownWideNarrowIcon size={14} />}
							<span className='truncate text-xs'>{sortLabel}</span>
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end' className='w-48'>
						<DropdownMenuLabel>Сортировка</DropdownMenuLabel>
						<DropdownMenuSeparator />
						{config.sortFields.map(opt => (
							<DropdownMenuItem
								key={opt.value}
								onClick={() => setSortBy(opt.value)}
								className={sortBy === opt.value ? 'bg-accent' : undefined}
							>
								{opt.label}
							</DropdownMenuItem>
						))}
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={() => setSortDir((sortDir === 'asc' ? 'desc' : 'asc') as SortDir)}>
							{sortDir === 'asc' ? 'По убыванию' : 'По возрастанию'}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>

				<Button variant='outline' size='sm' className='relative shrink-0 px-2.5' onClick={() => setFiltersOpen(true)}>
					<SlidersHorizontalIcon size={16} />
					{activeFilters > 0 && (
						<span className='absolute -top-1.5 -right-1.5 min-w-4 h-4 px-0.5 rounded-full bg-chart-2 text-background text-[10px] font-semibold flex items-center justify-center'>
							{activeFilters}
						</span>
					)}
				</Button>
			</div>

			{chips.length > 0 && (
				<div className='flex flex-wrap gap-1.5'>
					{chips.map(chip => (
						<button
							key={chip.key}
							type='button'
							onClick={() => updateFilter(clearFilterKey(chip.key))}
							className='inline-flex items-center gap-1 h-6 px-2 rounded-md bg-secondary text-xs text-secondary-foreground'
						>
							{chip.label}
							<XIcon size={12} className='opacity-60' />
						</button>
					))}
				</div>
			)}

			<ClientsFiltersSheet
				open={filtersOpen}
				onOpenChange={setFiltersOpen}
				filter={filter}
				filterSchema={config.filterSchema}
				activeFilters={activeFilters}
				onUpdate={updateFilter}
				onReset={() =>
					updateFilter({
						...defaultClientsFilter,
						onlineStatus: filter.onlineStatus,
					})
				}
			/>
		</div>
	);
}
